import { useRequest } from 'features/request/useRequest';
import { useAgGridDataSource } from 'hooks/useAgGridDataSource';
import { catalogUERBaseCostHistoryItemsGql } from 'pages/Catalogs/CatalogUERBaseCost/catalogUerBaseCostQueries';
import { destroyAgGridTableFilters, getDateOnly } from 'utils';

import type { AgGridReact } from 'ag-grid-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type { TFilter, TFilterType } from '../helpers';
import { getHistoryFilterValue, historyFilterColumnsFieldNames } from '../helpers';

import { useHistoryCounts } from './useHistoryCounts';

const requestId = 'catalogUERBaseCostHistoryItems';

export const useHistoryData = (catalogId: string | undefined) => {
    const tableRef = useRef<AgGridReact>(null);

    const [filterValue, setFilterValue] = useState<TFilter | null>(null);

    const countsObj = useHistoryCounts({
        catalogUniqueId: catalogId,
        endDate: filterValue?.dateFilter?.[1]?.toISOString(),
        startDate: filterValue?.dateFilter?.[0]?.toISOString(),
    });

    const handlerFilterChange = useCallback(
        ({ filterType, value }: { filterType?: TFilterType; value?: TFilter[TFilterType] } = {}) => {
            setFilterValue((prev) => {
                if (filterType === 'columnFilters') {
                    return {
                        ...prev,
                        [filterType]: {
                            ...prev?.[filterType],
                            ...(value as Record<string, any>),
                        },
                    } as TFilter;
                }

                if (filterType) {
                    tableRef?.current?.api?.onFilterChanged();

                    let newValue = value;

                    if (filterType === 'dateFilter') {
                        if (Array.isArray(value)) {
                            newValue = value.map((date, idx) => {
                                if (idx === 1 && date) {
                                    const nextDayDate = new Date(date);

                                    nextDayDate.setDate(nextDayDate.getDate() + 1);

                                    return nextDayDate;
                                }

                                return date;
                            }) as [Date?, Date?];
                        }
                    }

                    return {
                        ...prev,
                        [filterType]: newValue,
                    } as TFilter;
                }

                return prev;
            });
        },
        [],
    );

    useEffect(() => {
        if (!filterValue?.dateFilter) {
            tableRef?.current?.api?.showNoRowsOverlay();
        }
    }, [filterValue?.dateFilter]);

    const variables = useMemo(() => {
        if (catalogId && filterValue?.dateFilter) {
            return {
                catalogUniqueId: catalogId,
                where: getHistoryFilterValue(filterValue),
            };
        }

        return undefined;
    }, [catalogId, filterValue]);

    const [data, , , request] = useRequest({
        requestId,
        query: catalogUERBaseCostHistoryItemsGql,
        withEffect: false,
    });

    const { dataSource, getRowParams } = useAgGridDataSource({
        requestId,
        requestFn: request,
        requestVariables: variables,
    });

    useEffect(() => {
        if (data?.catalogUERBaseCostHistoryItems?.items && getRowParams?.current) {
            const { items, totalCount } = data.catalogUERBaseCostHistoryItems;

            getRowParams?.current?.successCallback(items, totalCount);
        }
    }, [data?.catalogUERBaseCostHistoryItems, getRowParams]);

    const dateFilterValues = useMemo(() => {
        if (filterValue?.dateFilter?.[0] && filterValue?.dateFilter?.[1]) {
            const fixedEndDate = new Date(filterValue?.dateFilter?.[1] ?? '');

            fixedEndDate.setDate(fixedEndDate.getDate() - 1);

            return {
                endDate: getDateOnly(fixedEndDate),
                endDateIso: filterValue?.dateFilter?.[1]?.toISOString(),
                startDate: getDateOnly(filterValue?.dateFilter?.[0]),
                startDateIso: filterValue?.dateFilter?.[0]?.toISOString(),
            };
        }

        return {
            endDate: undefined,
            endDateIso: undefined,
            startDate: undefined,
            startDateIso: undefined,
        };
    }, [filterValue?.dateFilter]);

    useEffect(() => {
        if (data?.catalogUERBaseCostHistoryItems?.totalCount) {
            destroyAgGridTableFilters({
                api: tableRef?.current?.api,
                columns: historyFilterColumnsFieldNames,
            });
        }
    }, [data?.catalogUERBaseCostHistoryItems?.totalCount, dateFilterValues]);

    return {
        dataSource,
        handlerFilterChange,
        tableRef,
        countsObj,
        tableRdy: Boolean(variables),
        dateFilterValues,
    };
};
